// src/components/Sidebar/MobileNavContext.jsx
//
// Estado compartido del menú móvil. Son dos cosas, y las dos tienen que vivir
// fuera de los componentes que las usan:
//
//   1. Si el cajón está abierto. Lo abre un botón que vive dentro de la
//      cabecera de la página y lo pinta el shell: no hay padre común más cerca
//      que el propio AppShell.
//   2. Quién dibuja la hamburguesa. En el diseño va en la misma línea que el
//      título de la página, así que la pinta `PageHeader`; pero hay rutas que
//      no montan ninguna cabecera —Zeno, Home— y ahí el shell tiene que poner
//      una flotante o el cajón queda inalcanzable. Las cabeceras montadas se
//      registran aquí, y el shell dibuja la suya sólo cuando no hay ninguna.
//      Si por lo que sea hay dos cabeceras a la vez, sólo la primera pinta el
//      botón: dos hamburguesas en la misma esquina es peor que ninguna.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Fuera del shell (una vista suelta, un test que monta `PageHeader` a pelo) el
// menú no existe: el hook devuelve algo inerte en vez de reventar.
const NOOP_NAV = {
  open: false,
  openMenu: () => {},
  closeMenu: () => {},
  registerHeader: () => () => {},
  primaryHeaderId: null,
  insideShell: false,
};

const MobileNavContext = createContext(NOOP_NAV);

let nextHeaderId = 0;

export function MobileNavProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [headers, setHeaders] = useState([]);

  const openMenu = useCallback(() => setOpen(true), []);
  const closeMenu = useCallback(() => setOpen(false), []);

  const registerHeader = useCallback((id) => {
    setHeaders((ids) => [...ids, id]);
    return () => setHeaders((ids) => ids.filter((x) => x !== id));
  }, []);

  const value = useMemo(
    () => ({
      open,
      openMenu,
      closeMenu,
      registerHeader,
      primaryHeaderId: headers[0] ?? null,
      insideShell: true,
    }),
    [open, openMenu, closeMenu, registerHeader, headers]
  );

  return (
    <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>
  );
}

export function useMobileNav() {
  return useContext(MobileNavContext);
}

/**
 * Para una cabecera de página: se registra mientras esté montada y responde si
 * le toca a ella pintar la hamburguesa.
 *
 * `enabled` deja fuera a las cabeceras que no lo son —una tira de controles
 * embebida dentro de una sección que ya tiene la suya—. Registrarlas movería la
 * hamburguesa a media página.
 */
export function useHeaderSlot(enabled = true) {
  const { registerHeader, primaryHeaderId } = useMobileNav();
  const idRef = useRef(null);
  if (idRef.current === null) {
    nextHeaderId += 1;
    idRef.current = `header-${nextHeaderId}`;
  }

  useEffect(() => {
    if (!enabled) return undefined;
    return registerHeader(idRef.current);
  }, [registerHeader, enabled]);

  return enabled && primaryHeaderId === idRef.current;
}

export default MobileNavContext;
